import React from 'react';
import { theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { BookingStatus } from '../interfaces/heavyDutyMachinery/IBooking';
import { BOOKING_STATUS_ICON, BOOKING_STATUS_LABEL_KEYS, BOOKING_STATUS_TONES } from '../constants/bookingStatusOptions';
import { pillToneStyle } from '../theme/creamyTokens';
import { translate } from '../helpers/translationHelper';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

// Same statuses BOOKING_STATUS_ICON/BOOKING_STATUS_TONES already cover, in
// that file's own display order.
const ALL_STATUSES: BookingStatus[] = [
  'Pending',
  'AwaitingQuote',
  'AwaitingPayment',
  'Confirmed',
  'Completed',
  'Cancelled',
  'HostNoShow',
  'GuestNoShow',
];

interface BookingStatusFilterProps {
  // Empty means "no filter" -- every status shows, same as never having
  // touched this row. Never treated as "show nothing".
  value: BookingStatus[];
  onChange: (value: BookingStatus[]) => void;
}

/**
 * Multi-select status filter, shared by MyBookings' guest view and
 * IncomingBookings (the host's "Entrantes" list and Calendario tab alike,
 * since both are fed by the same filtered array).
 *
 * Every chip carries its own status colour at rest -- the exact tone
 * (pillToneStyle/BOOKING_STATUS_TONES) BookingCard's status pill uses for
 * that status, so "Confirmada" is the same green here and there. They used
 * to be neutral grey until selected, which meant the row gave you no way to
 * find the one you wanted by colour, only by reading eight labels. Selecting
 * one adds a solid ring in the same tone rather than introducing a second
 * colour.
 *
 * On a phone the row scrolls sideways and you swipe it. On a desktop it
 * wraps: the scrollbar is hidden by the `no-scrollbar` convention, so
 * overflowing there meant the last chips were simply cut off against the
 * right edge with nothing to say they were there and no way to reach them
 * without a touchscreen.
 */
const BookingStatusFilter: React.FC<BookingStatusFilterProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();

  const toggle = (status: BookingStatus) => {
    onChange(value.includes(status) ? value.filter((s) => s !== status) : [...value, status]);
  };

  return (
    <div
      className={isMobile ? 'no-scrollbar' : undefined}
      style={{
        display: 'flex',
        gap: 8,
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        overflowX: isMobile ? 'auto' : 'visible',
        paddingBottom: 4,
        marginBottom: 16,
        ...(isMobile ? { scrollbarWidth: 'none' as const, msOverflowStyle: 'none' as const } : {}),
      }}
    >
      {ALL_STATUSES.map((status) => {
        const selected = value.includes(status);
        const Icon = BOOKING_STATUS_ICON[status];
        const toneStyle = pillToneStyle(BOOKING_STATUS_TONES[status], token);
        return (
          <button
            key={status}
            type="button"
            aria-pressed={selected}
            onClick={() => toggle(status)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexShrink: 0,
              padding: '6px 12px',
              borderRadius: 999,
              // The tone is always on: what changes when you pick one is the
              // ring around it, not whether it has a colour at all.
              border: `1.5px solid ${selected ? toneStyle.color : 'transparent'}`,
              background: toneStyle.background,
              color: toneStyle.color,
              fontWeight: selected ? 600 : 400,
              opacity: selected ? 1 : 0.75,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
            }}
          >
            <Icon size={14} />
            <span style={{ fontSize: 13 }}>{translate(t, BOOKING_STATUS_LABEL_KEYS[status])}</span>
          </button>
        );
      })}
    </div>
  );
};

export default BookingStatusFilter;
